class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', () => {
      this.close();
    });
    this.setHeaderCartIconAccessibility();
    // Delegated on <cart-drawer>: the drawer's inner HTML (form included) is replaced on every
    // cart re-render, so a listener bound to the form itself would be lost after the first update.
    this.addEventListener('submit', this.onCheckoutSubmit.bind(this));
  }

  onCheckoutSubmit(event) {
    if (event.target.id !== 'CartDrawer-Form') return;
    // Read the current form: section rendering replaces it after cart updates.
    // The fraud-block Function enforces this independently in Shopify Checkout.
    if (event.target.dataset.checkoutBlocked === 'true') {
      event.preventDefault();
      return;
    }
    const errors = this.querySelector('#CartDrawer-CartErrors');
    const violations = this.findLimitViolations();
    if (violations.length) {
      // Client-side guard; the checkout validation function enforces the same limits server-side.
      event.preventDefault();
      violations.forEach((violation) => {
        violation.lines.forEach((li) => {
          const lineError = li.querySelector('.cart-item__error-text');
          if (lineError) lineError.textContent = purchaseLimitMessage(violation.limit);
        });
      });
      if (errors) errors.textContent = purchaseLimitMessage(violations[0].limit);
      return;
    }
    if (errors) errors.textContent = '';
    const button = this.querySelector('#CartDrawer-Checkout');
    if (button) button.classList.add('is-loading');
  }

  // Sums, per product, the quantities the checkout form is about to POST (the updates[] inputs) and
  // compares them with the per-line data-limit rendered by Liquid from the product tags.
  findLimitViolations() {
    const cart = readCartData();
    const totals = {};
    this.querySelectorAll('li[data-limit]').forEach((li) => {
      const limit = parseInt(li.dataset.limit, 10);
      if (!(limit > 0)) return;
      const input = li.querySelector('input[name="updates[]"]');
      const item = (cart.items || []).find((i) => i.key === li.dataset.lineKey);
      let quantity = item ? item.quantity : 0;
      if (input) quantity = parseInt(input.value, 10) || 0;
      const productId = li.dataset.productId;
      totals[productId] = totals[productId] || { limit, quantity: 0, lines: [] };
      totals[productId].quantity += quantity;
      totals[productId].lines.push(li);
    });
    return Object.values(totals).filter((total) => total.quantity > total.limit);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
